import type { RetrievedGmbAuditReport } from '@/pages/gmb-audit/data/auditApi';
import {
  buildMetricComparisons,
  formatDelta,
  formatMetricSide,
  formatScoreDelta,
  numericDelta,
} from '@/pages/gmb-audit/utils/historyComparison';

interface HistoryComparisonSectionProps {
  oldReport: RetrievedGmbAuditReport;
  newReport: RetrievedGmbAuditReport;
}

function formatDate(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}/${pad(d.getMonth() + 1)}/${pad(d.getDate())}`;
}

function formatScoreValue(score: number | undefined): string {
  return typeof score === 'number' ? `${score} / 100` : '未計分';
}

function formatRating(value: number | undefined): string {
  return typeof value === 'number' ? value.toFixed(1) : '尚無公開評分';
}

function formatCount(value: number | undefined): string {
  return typeof value === 'number' ? String(value) : '尚無資料';
}

/** 前後比較主體：歷史 KPI 變化 + 11 項指標（桌面 2 欄/表格、行動單欄）。 */
export default function HistoryComparisonSection({
  oldReport,
  newReport,
}: HistoryComparisonSectionProps) {
  const sameScoring = oldReport.scoringModelVersion === newReport.scoringModelVersion;
  const samePeer = oldReport.peerModelVersion === newReport.peerModelVersion;

  const oldScoreResult = oldReport.report.scoreResult;
  const newScoreResult = newReport.report.scoreResult;

  const scoreDelta =
    sameScoring && typeof oldScoreResult.score === 'number' && typeof newScoreResult.score === 'number'
      ? numericDelta(oldScoreResult.score, newScoreResult.score)
      : null;

  const oldTarget = oldReport.report.target;
  const newTarget = newReport.report.target;
  const ratingDelta = numericDelta(oldTarget.rating, newTarget.rating);
  const reviewDelta = numericDelta(oldTarget.userRatingCount, newTarget.userRatingCount);

  const oldPeer = oldReport.report.peerComparison;
  const newPeer = newReport.report.peerComparison;
  const canComparePeer = Boolean(oldPeer && newPeer && samePeer);
  const peerRatingDelta = canComparePeer
    ? numericDelta(oldPeer?.ratingMedian, newPeer?.ratingMedian)
    : null;
  const peerReviewDelta = canComparePeer
    ? numericDelta(oldPeer?.reviewCountMedian, newPeer?.reviewCountMedian)
    : null;

  const metricRows = buildMetricComparisons(oldReport, newReport, sameScoring);

  return (
    <div className="mt-5 rounded-2xl border border-background-300 bg-background-50 p-4 shadow-card md:p-6">
      <div className="flex items-center gap-2">
        <i className="ri-arrow-left-right-line text-lg text-primary-700" />
        <h3 className="text-base font-bold text-foreground-950 md:text-lg">歷史 KPI 變化</h3>
      </div>

      {/* 版本警告（模型守門） */}
      {!sameScoring && (
        <div className="mt-3 flex items-start gap-2.5 rounded-xl border border-accent-200 bg-accent-50/70 p-3 text-accent-900">
          <i className="ri-alert-line mt-0.5 text-base" />
          <p className="text-sm leading-relaxed md:text-base">
            評分模型版本不同，不直接比較分數變化。以下仍並列顯示兩次分數，但不標示增減。
          </p>
        </div>
      )}
      {!samePeer && (
        <div className="mt-3 flex items-start gap-2.5 rounded-xl border border-accent-200 bg-accent-50/70 p-3 text-accent-900">
          <i className="ri-alert-line mt-0.5 text-base" />
          <p className="text-sm leading-relaxed md:text-base">
            兩次健診使用不同版本的同業比較模型，同業基準僅供個別參考，不計算基準增減。
          </p>
        </div>
      )}

      {/* 兩次健診時間 */}
      <div className="mt-4 grid grid-cols-1 gap-3 sm:grid-cols-2">
        <div className="rounded-xl border border-background-300 bg-background-100 p-3">
          <p className="text-sm text-foreground-500">較早健診</p>
          <p className="mt-0.5 text-sm font-semibold text-foreground-800">{formatDate(oldReport.createdAt)}</p>
        </div>
        <div className="rounded-xl border border-primary-200 bg-primary-50 p-3">
          <p className="text-sm text-primary-700">較新健診</p>
          <p className="mt-0.5 text-sm font-semibold text-foreground-800">{formatDate(newReport.createdAt)}</p>
        </div>
      </div>

      {/* KPI delta rows */}
      <div className="mt-4 overflow-hidden rounded-2xl border border-background-200">
        <div className="grid grid-cols-1 divide-y divide-background-200 md:grid-cols-4 md:divide-y-0">
          <SummaryCell
            label="健康度分數"
            oldText={formatScoreValue(oldScoreResult.score)}
            newText={formatScoreValue(newScoreResult.score)}
            deltaText={sameScoring ? formatScoreDelta(scoreDelta) : '版本不同'}
            deltaValue={sameScoring ? scoreDelta : null}
          />
          <SummaryCell
            label="健康度等級"
            oldText={oldScoreResult.healthGrade ?? '—'}
            newText={newScoreResult.healthGrade ?? '—'}
            deltaText=""
            deltaValue={null}
          />
          <SummaryCell
            label="評分資料覆蓋率"
            oldText={`${oldScoreResult.dataCoveragePercent}%`}
            newText={`${newScoreResult.dataCoveragePercent}%`}
            deltaText={formatDelta(numericDelta(oldScoreResult.dataCoveragePercent, newScoreResult.dataCoveragePercent), '%')}
            deltaValue={numericDelta(oldScoreResult.dataCoveragePercent, newScoreResult.dataCoveragePercent)}
          />
          <SummaryCell
            label="Google 評分"
            oldText={formatRating(oldTarget.rating)}
            newText={formatRating(newTarget.rating)}
            deltaText={ratingDelta === null ? '' : formatDelta(ratingDelta)}
            deltaValue={ratingDelta}
          />
        </div>
        <div className="grid grid-cols-1 border-t border-background-200 md:grid-cols-4">
          <SummaryCell
            label="Google 評論數"
            oldText={formatCount(oldTarget.userRatingCount)}
            newText={formatCount(newTarget.userRatingCount)}
            deltaText={reviewDelta === null ? '' : formatDelta(reviewDelta, ' 則')}
            deltaValue={reviewDelta}
          />
          <SummaryCell
            label="同業評分中位數"
            oldText={formatRating(oldPeer?.ratingMedian)}
            newText={formatRating(newPeer?.ratingMedian)}
            deltaText={peerRatingDelta === null ? (samePeer ? '' : '版本不同') : formatDelta(peerRatingDelta)}
            deltaValue={peerRatingDelta}
          />
          <SummaryCell
            label="同業評論數中位數"
            oldText={oldPeer ? formatCount(oldPeer.reviewCountMedian) : '尚無資料'}
            newText={newPeer ? formatCount(newPeer.reviewCountMedian) : '尚無資料'}
            deltaText={peerReviewDelta === null ? (samePeer ? '' : '版本不同') : formatDelta(peerReviewDelta, ' 則')}
            deltaValue={peerReviewDelta}
          />
          <div className="hidden md:block" />
        </div>
      </div>

      {/* 11 項指標 */}
      <div className="mt-6">
        <h4 className="text-base font-bold text-foreground-950">11 項指標前後比較</h4>
        <div className="mt-3 overflow-hidden rounded-2xl border border-background-200">
          <div className="hidden bg-background-100 px-4 py-2.5 text-sm font-semibold text-foreground-500 md:grid md:grid-cols-4">
            <span>指標</span>
            <span>較早</span>
            <span>較新</span>
            <span className="text-right">變化</span>
          </div>
          <div className="divide-y divide-background-200">
            {metricRows.map((row) => (
              <div key={row.key} className="grid grid-cols-1 gap-1.5 px-4 py-3 md:grid-cols-4 md:items-center md:gap-2">
                <span className="text-sm font-medium text-foreground-900">{row.label}</span>
                <span className="text-sm text-foreground-700 md:text-left">
                  <span className="mr-1 text-sm text-foreground-400 md:hidden">較早：</span>
                  {formatMetricSide(row.oldScore, row.oldMaxScore, row.oldStatus)}
                </span>
                <span className="text-sm text-foreground-700 md:text-left">
                  <span className="mr-1 text-sm text-foreground-400 md:hidden">較新：</span>
                  {formatMetricSide(row.newScore, row.newMaxScore, row.newStatus)}
                </span>
                <span className="text-sm md:text-right">
                  {row.comparable ? (
                    <MetricDelta delta={row.delta} />
                  ) : (
                    <span className="text-sm text-foreground-400">
                      {sameScoring ? '無法直接比較' : '版本不同'}
                    </span>
                  )}
                </span>
              </div>
            ))}
          </div>
        </div>
        <p className="mt-2 text-sm text-foreground-400">
          「資料不足」代表該次健診該項無足夠公開資料，未以 0 分計算，因此不與另一側直接相減。
        </p>
      </div>
    </div>
  );
}

function SummaryCell({
  label,
  oldText,
  newText,
  deltaText,
  deltaValue,
}: {
  label: string;
  oldText: string;
  newText: string;
  deltaText: string;
  deltaValue: number | null;
}) {
  return (
    <div className="p-3">
      <p className="text-sm text-foreground-500">{label}</p>
      <div className="mt-1 flex flex-wrap items-center gap-x-2 gap-y-0.5 text-sm">
        <span className="text-foreground-500">{oldText}</span>
        <i className="ri-arrow-right-line text-foreground-300" />
        <span className="font-semibold text-foreground-900">{newText}</span>
      </div>
      {deltaText && (
        <p className="mt-1">
          {deltaValue === null ? (
            <span className="text-sm text-foreground-400">{deltaText}</span>
          ) : (
            <MetricDelta delta={deltaValue} />
          )}
        </p>
      )}
    </div>
  );
}

function MetricDelta({ delta }: { delta: number | null }) {
  if (delta === null) return <span className="text-sm text-foreground-400">無法直接比較</span>;
  if (delta === 0) return <span className="text-sm font-medium text-foreground-500">無變化</span>;
  if (delta > 0) {
    return (
      <span className="inline-flex items-center gap-1 text-sm font-semibold text-primary-700">
        <i className="ri-arrow-up-line" />
        {`+${delta}`}
      </span>
    );
  }
  return (
    <span className="inline-flex items-center gap-1 text-sm font-semibold text-rose-600">
      <i className="ri-arrow-down-line" />
      {`${delta}`}
    </span>
  );
}